import { Injectable } from '@angular/core';
import { ApiService } from '../../../../_core/_services/api.service';
import { EntityServiceInterface } from '../entity.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable()
export abstract class BaseEntityService<T> implements EntityServiceInterface {
  // Data observable
  data$ = new BehaviorSubject<T[]>([]);

  // Current data
  protected data: T[] = [];

  // Loading state
  protected loading = false;

  // Abstract property that subclasses must implement
  protected abstract endpoint: string;

  constructor(protected api: ApiService) {}

  // Get data from API
  getData(queryParams?: any): Observable<any> {
    this.loading = true;
    console.log(`Loading data for ${this.endpoint} with params:`, queryParams);

    // Build default parameters
    const defaultParams = {
      limit: 100,
      offset: 0,
      q: '',
      filter: []
    };

    // Merge query params with defaults, but preserve specific query params
    const params = { ...defaultParams, ...queryParams };

    // Handle search parameter mapping
    if (params.search && !params.q) {
      params.q = params.search;
      delete params.search;
    }

    console.log(`Final params for ${this.endpoint}:`, params);

    return this.api.getAPI<T>(this.endpoint, params).pipe(
      map((res: any) => {
        console.log(`=== BASE ENTITY SERVICE RESPONSE FOR ${this.endpoint} ===`);
        console.log('Full response:', res);
        console.log('Response body:', res.body);
        console.log('Response body type:', typeof res.body);
        console.log('Response body is array:', Array.isArray(res.body));

        // Most list endpoints return { data: [...] }, but some (e.g. /region)
        // return { results: [...] } instead - fall back to whichever is present.
        const data = res.body?.data ?? res.body?.results ?? [];
        console.log(`Extracted data for ${this.endpoint}:`, data);
        console.log(`Data type:`, typeof data);
        console.log(`Data is array:`, Array.isArray(data));
        console.log(`Data length:`, Array.isArray(data) ? data.length : 'not array');

        // For search queries, replace the data entirely with filtered results
        // For ID queries, merge with existing data to preserve other items
        if (params.id && !params.q) {
          console.log('ID query detected (no search), merging with existing data');
          const existingData = [...this.data];
          const newData = Array.isArray(data) ? data : [];

          // Merge new data with existing data, avoiding duplicates
          newData.forEach((newItem: any) => {
            const existingIndex = existingData.findIndex((item: any) => item.id === newItem.id);
            if (existingIndex >= 0) {
              existingData[existingIndex] = newItem; // Update existing item
            } else {
              existingData.push(newItem); // Add new item
            }
          });

          this.data = existingData;
        } else {
          // Regular load or search query, replace all data
          console.log('Regular load or search query detected, replacing all data');
          this.data = data;
        }

        this.data$.next(this.data);
        this.loading = false;
        console.log(
          `Data observable updated for ${this.endpoint}, current count:`,
          this.data.length
        );
        console.log(`Returning from base entity service:`, res.body);
        // Callers throughout the app read the list off `.data` (the shape
        // most list endpoints use) - normalize it here so endpoints that
        // instead return `.results` (e.g. /region) don't silently look empty
        // to every subscriber.
        return { ...res.body, data: this.data };
      })
    );
  }

  // Add new data
  addData(data: T): Observable<any> {
    return this.api.postAPI<T>(this.endpoint, data).pipe(
      map((res: any) => {
        const newItem = res.body;
        this.data.push(newItem);
        this.data$.next([...this.data]);
        return res;
      })
    );
  }

  // Update data
  updateData(id: number, data: T): Observable<any> {
    console.log('=== BASE ENTITY SERVICE UPDATE DEBUG ===');
    console.log('BaseEntityService.updateData called for endpoint:', this.endpoint);
    console.log('Update ID:', id);
    console.log('Update data:', data);

    return this.api.putAPI<T>(this.endpoint, id, data).pipe(
      map((res: any) => {
        console.log('=== BASE ENTITY SERVICE UPDATE SUCCESS ===');
        console.log('API response:', res);
        const updatedItem = res.body;
        console.log('Updated item from response:', updatedItem);

        const index = this.data.findIndex((item) => (item as any).id === id);
        console.log('Found item at index:', index);

        if (index !== -1) {
          this.data[index] = updatedItem;
          this.data$.next([...this.data]);
          console.log('Data updated successfully, new count:', this.data.length);
        } else {
          console.warn('Item not found in local data, adding it');
          this.data.push(updatedItem);
          this.data$.next([...this.data]);
        }
        return res;
      }),
      catchError((error) => {
        console.log('=== BASE ENTITY SERVICE UPDATE ERROR ===');
        console.error('Update error in BaseEntityService:', error);
        throw error;
      })
    );
  }

  // Delete data
  deleteData(id: number): Observable<any> {
    return this.api.deleteAPI<T>(this.endpoint, id).pipe(
      map((res: any) => {
        const index = this.data.findIndex((item) => (item as any).id === id);
        if (index !== -1) {
          this.data.splice(index, 1);
          this.data$.next([...this.data]);
        }
        return res;
      })
    );
  }

  // Refresh data
  refreshData(): void {
    this.getData().subscribe();
  }

  // Refresh data with parameters to preserve state
  refreshDataWithParams(queryParams?: any): void {
    this.getData(queryParams).subscribe();
  }

  // Get current data
  getCurrentData(): T[] {
    return this.data;
  }

  // Check if loading
  isLoading(): boolean {
    return this.loading;
  }
}
